import * as THREE from 'three';
import type { InputManager } from '../../input';
import type { PlayerShip } from '../ships';
import type { ShipControlSystem } from '../ships/ShipControlSystem';
import type { PlayerStats } from '../player';
import type { MapBody } from '../../scenes';
import type { JumpHud } from '../../ui/JumpHud';
import type { AudioManager } from '../../audio';
import { WarpStreaks } from '../../visual';

const CHARGE_TIME = 3;
const SPEED_MULTIPLIER = 5;
/** Per-second consumption multiplier vs full-thrust normal flight. With ship
 *  also moving 5× faster, this works out to a 5× cost per *unit distance*
 *  travelled — the "5× fuel" tradeoff the player accepts for the speed. */
const FUEL_MULTIPLIER = 25;
/** Flat fuel cost paid the moment the jump kicks in, so even a short hop has
 *  a noticeable price tag. Tuned as a fraction of fuelMax. */
const IGNITION_FUEL_FRACTION = 0.05;
/** cos(~23°) — the nose has to be roughly pointing at the target to engage. */
const FACING_THRESHOLD = 0.92;
/** Slerp coefficient per second for auto-aim. Bigger = snappier lock. */
const AIM_RESPONSE = 6;
/** FOV (degrees) the camera is pushed to during a jump for that
 *  near-light-speed wide-angle feel. Smoothly tweens in and out. */
const JUMP_FOV_TARGET = 96;
/** How quickly FOV approaches its target (per-second slerp coefficient). */
const FOV_RESPONSE = 4;

export type JumpState = 'idle' | 'charging' | 'jumping';

export interface JumpControllerDeps {
  readonly ship: PlayerShip;
  readonly shipControl: ShipControlSystem;
  readonly input: InputManager;
  readonly stats: PlayerStats;
  /** Player ship base cruise speed and base fuel rate — we multiply these. */
  readonly baseMaxSpeed: number;
  readonly baseFuelPerSecond: number;
  /** Current waypoint, or null if none marked. */
  readonly getDestination: () => MapBody | null;
  /** Distance from a body's center at which the jump auto-completes. */
  readonly getArrivalRadius: (body: MapBody) => number;
  readonly hud: JumpHud;
  readonly audio: AudioManager;
  readonly camera: THREE.Camera;
  /** Scene root the warp-streaks effect attaches to (under the ship). */
  readonly warpStreaks: WarpStreaks;
  /** Force the chase camera to its desired follow position+lookAt now,
   *  bypassing FollowController's lerp. Provided by GameDirector since the
   *  controller instance is owned there. */
  readonly snapCamera: () => void;
  /** Optional: fired when a charge actually begins (post facing /
   *  fuel checks). Used by the tutorial mission system to advance
   *  the "charge the jump" step. `destinationId` matches the
   *  destination's `MapBody.id`. */
  readonly onChargeStarted?: (destinationId: string) => void;
  /** Optional: fired when a jump completes successfully (ship arrived
   *  inside `arrivalRadius`). Not fired on cancel or fuel-out aborts. */
  readonly onJumpCompleted?: (destinationId: string) => void;
}

/** Hyperspace jump state machine. Owned by GameDirector and ticked from its
 *  space-flying update. Locks ShipControlSystem during charge + jump so the
 *  player can't fight the autopilot, and restores control on cancel/arrival. */
export class JumpController {
  private state: JumpState = 'idle';
  private chargeTimer = 0;

  private readonly tmpForward = new THREE.Vector3();
  private readonly tmpDir = new THREE.Vector3();
  private readonly tmpTargetPos = new THREE.Vector3();
  private readonly tmpLookMatrix = new THREE.Matrix4();
  private readonly tmpUp = new THREE.Vector3(0, 1, 0);
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly baseFov: number;
  /** Whether the jump-drive loop sound is currently playing. */
  private droneActive = false;

  constructor(private readonly deps: JumpControllerDeps) {
    // Capture the camera's resting FOV once so we always restore to it,
    // even if other systems momentarily change it.
    this.baseFov =
      deps.camera instanceof THREE.PerspectiveCamera ? deps.camera.fov : 72;
    deps.ship.object3D.add(deps.warpStreaks.object3D);
  }

  isActive(): boolean {
    return this.state !== 'idle';
  }

  /** Force-cancel from the outside (e.g. the player switched scenes). */
  forceCancel(): void {
    if (this.state === 'idle') return;
    this.endAndRestoreControl();
  }

  update(dt: number): void {
    // Cosmetic state (FOV + warp streaks) ticks every frame regardless of
    // jump state, so they ease back to neutral *after* the jump ends too.
    this.tickFov(dt);
    this.deps.warpStreaks.update(this.deps.baseMaxSpeed * SPEED_MULTIPLIER, dt);

    const jPressed = this.deps.input.isKeyJustPressed('KeyJ');

    if (this.state === 'idle') {
      if (jPressed) this.tryStart();
      return;
    }

    // Charging or jumping — J always cancels.
    if (jPressed) {
      this.endAndRestoreControl();
      this.deps.hud.hide();
      return;
    }

    const dest = this.deps.getDestination();
    if (!dest) {
      // Destination removed mid-jump — abort.
      this.endAndRestoreControl();
      this.deps.hud.hide();
      return;
    }

    if (this.state === 'charging') this.tickCharging(dt, dest);
    else if (this.state === 'jumping') this.tickJumping(dt, dest);
  }

  private tryStart(): void {
    const dest = this.deps.getDestination();
    if (!dest) {
      this.deps.hud.showError('NO DESTINATION SET');
      return;
    }
    if (this.deps.stats.fuel <= 0) {
      this.deps.hud.showError('NO FUEL');
      return;
    }

    const ship = this.deps.ship;
    dest.getWorldPosition(this.tmpTargetPos);

    // Already inside the arrival ring? Don't pretend to jump.
    const distance = this.tmpTargetPos.distanceTo(ship.object3D.position);
    if (distance <= this.deps.getArrivalRadius(dest)) {
      this.deps.hud.showError('ALREADY IN ORBIT');
      return;
    }

    // Facing check — ship's local -Z must roughly point at the target.
    this.tmpForward.set(0, 0, -1).applyQuaternion(ship.object3D.quaternion);
    this.tmpDir
      .copy(this.tmpTargetPos)
      .sub(ship.object3D.position)
      .normalize();
    if (this.tmpForward.dot(this.tmpDir) < FACING_THRESHOLD) {
      this.deps.hud.showError('AIM AT DESTINATION');
      return;
    }

    this.beginCharging();
  }

  private beginCharging(): void {
    this.state = 'charging';
    this.chargeTimer = CHARGE_TIME;
    this.deps.shipControl.setEnabled(false);
    this.deps.hud.showCountdown(this.chargeTimer);
    const dest = this.deps.getDestination();
    if (dest) this.deps.onChargeStarted?.(dest.id);
  }

  private tickCharging(dt: number, dest: MapBody): void {
    this.chargeTimer -= dt;
    this.alignShipToward(dest, dt);
    this.snapCameraToShip();
    this.deps.hud.showCountdown(this.chargeTimer);
    if (this.chargeTimer <= 0) this.beginJumping();
  }

  private beginJumping(): void {
    this.state = 'jumping';
    // Zero residual velocity so the ship moves at exactly jump speed via the
    // direct position step below — no accumulated drift to fight.
    this.deps.ship.velocity.set(0, 0, 0);

    // Audio: sonic-boom impact at the moment of departure, then a looped
    // imposing drive sound that runs for the duration of the jump.
    this.deps.audio.playSonicBoom();
    this.deps.audio.startJumpDrive();
    this.droneActive = true;

    // Visual: kick on the warp tunnel.
    this.deps.warpStreaks.start();

    // Up-front fuel hit so even short jumps cost something noticeable.
    this.deps.stats.consumeFuel(
      this.deps.stats.fuelMax * IGNITION_FUEL_FRACTION
    );
  }

  private tickJumping(dt: number, dest: MapBody): void {
    if (this.deps.stats.fuel <= 0) {
      this.deps.hud.showError('FUEL DEPLETED');
      this.endAndRestoreControl();
      return;
    }

    const ship = this.deps.ship;
    dest.getWorldPosition(this.tmpTargetPos);
    const distance = this.tmpTargetPos.distanceTo(ship.object3D.position);

    if (distance <= this.deps.getArrivalRadius(dest)) {
      this.deps.hud.hide();
      this.endAndRestoreControl();
      this.deps.onJumpCompleted?.(dest.id);
      return;
    }

    this.alignShipToward(dest, dt);

    const speed = this.deps.baseMaxSpeed * SPEED_MULTIPLIER;
    this.tmpForward.set(0, 0, -1).applyQuaternion(ship.object3D.quaternion);
    ship.object3D.position.addScaledVector(this.tmpForward, speed * dt);

    // Hyperspace velocity (650 u/s) overruns FollowController's positionLerp,
    // so the chase camera trails by ~130 units behind the desired follow
    // position. Snap it directly so the ship stays at the same on-screen
    // distance as during normal flight.
    this.snapCameraToShip();

    this.deps.stats.consumeFuel(
      this.deps.baseFuelPerSecond * FUEL_MULTIPLIER * dt
    );

    ship.setThrust(1);
    this.deps.hud.showJumping(distance);
  }

  /** Place the chase camera directly at its desired follow position+lookAt,
   *  bypassing FollowController's lerps. Used during charging and jumping
   *  where ship velocity overruns the smoothing rates and the smoothedLook
   *  lags so far behind it ends up *behind* the ship — pointing the camera
   *  at the back of the ship. */
  private snapCameraToShip(): void {
    this.deps.snapCamera();
  }

  private alignShipToward(dest: MapBody, dt: number): void {
    const ship = this.deps.ship;
    dest.getWorldPosition(this.tmpTargetPos);

    // Three.js convention: lookAt aligns local -Z with the target — the same
    // axis ShipControlSystem treats as forward — so the ship's nose points
    // exactly down the jump vector.
    this.tmpLookMatrix.lookAt(
      ship.object3D.position,
      this.tmpTargetPos,
      this.tmpUp
    );
    this.tmpQuat.setFromRotationMatrix(this.tmpLookMatrix);
    const t = 1 - Math.exp(-AIM_RESPONSE * dt);
    ship.object3D.quaternion.slerp(this.tmpQuat, t);
  }

  /** Tear down the autopilot and hand control back to the pilot. The HUD
   *  caller decides whether to clear or replace the message — arrival hides
   *  it, errors leave the toast briefly visible. */
  private endAndRestoreControl(): void {
    const wasJumping = this.state === 'jumping';
    this.state = 'idle';
    this.chargeTimer = 0;
    this.deps.ship.setThrust(0);
    // Sync ShipControlSystem's internal yaw/pitch to the current orientation
    // so re-enabling control doesn't snap back to a stale heading.
    this.deps.shipControl.syncFromShip();
    this.deps.shipControl.setEnabled(true);

    // Audio: only play the arrival boom if we were actually jumping (not on
    // a charge cancel). Always make sure the drive loop is killed.
    if (wasJumping) this.deps.audio.playSonicBoom();
    if (this.droneActive) {
      this.deps.audio.stopJumpDrive();
      this.droneActive = false;
    }

    // Visual: warp tunnel fades out via its own intensity tween.
    this.deps.warpStreaks.stop();
  }

  /** Tween camera FOV toward the active jump target while charging or
   *  jumping; ease back to base afterwards. */
  private tickFov(dt: number): void {
    const cam = this.deps.camera;
    if (!(cam instanceof THREE.PerspectiveCamera)) return;
    // Charge widens FOV partially (build-up); the jump itself widens it fully.
    const target =
      this.state === 'jumping'
        ? JUMP_FOV_TARGET
        : this.state === 'charging'
          ? this.baseFov + (JUMP_FOV_TARGET - this.baseFov) * 0.25
          : this.baseFov;
    if (Math.abs(cam.fov - target) < 0.01) return;
    const k = 1 - Math.exp(-FOV_RESPONSE * dt);
    cam.fov += (target - cam.fov) * k;
    cam.updateProjectionMatrix();
  }
}
